import { Back } from '@/components/Back'
import { Block } from '@/components/Block'
import { Button } from '@/components/Button'
import { Modal } from '@/components/Modal'
import { Title } from '@/components/Title'
import {
  licenseVersionSelector,
  pricesSelector,
} from '@/core/services/LicenseVersions/store/license-versions.selector'
import {
  getLicenseVersionsByIdThunk,
  licenseVersionsPriceWithOptionsThunk,
  setLicenseVersion,
} from '@/core/services/LicenseVersions/store/license-versions.slice'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { RoleUserModel } from '@/core/services/Token/model/TokenModel'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { Memo } from '@/hoc/Memo'
import useBackendError from '@/hooks/useBackendError'
import { makeFraction } from '@/utils/makeFraction'
import { setItem } from '@/utils/persistanceStorage'
import { cn } from '@bem-react/classname'
import { parse, stringify } from 'query-string'
import { FC, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  roleSelector,
  userRoleSelector,
} from '../AccountPage/store/accont.selector'
import { CustomerModel } from './models/LicensesModel'
import {
  licensesFreeByDiscountCodeThunk,
  licensesTrialByDeviceSerialThunk,
} from './store/licenses.slice'

const cnSubscriptionPage = cn('SubscriptionPage')

import './styles/SubscriptionPage.scss'
import {
  DG_RESELLER_ID,
  LICENSE_VERSION_LABELS,
  MODEM_4G_PRODUCT_LINK,
} from '@/config/constants'
import { Checkbox } from '@/components/form/Checkbox'

export const SubscriptionPage: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()
  const { search } = useLocation()
  const licenseVersion = useAppSelector(licenseVersionSelector)
  const prices = useAppSelector(pricesSelector)
  const role = useAppSelector(roleSelector)
  const userRole = useAppSelector(userRoleSelector)
  const [successFreeModal, setSuccessFreeModal] = useState(false)

  const [currentPeriod, setCurrentPeriod] = useState<string>('')
  const [isCreatedByDg, setIsCreatedByDg] = useState<boolean>(false)
  const [isRenew, setIsRenew] = useState<boolean>(false)
  const [licenseResellerId, setLicenseResellerId] = useState<string>('')

  const isAnyPriceFree = prices?.prices.some(price => price.isFree)

  const isCreatedByDGOption =
    userRole === RoleUserModel.SuperUser &&
    isAnyPriceFree &&
    licenseResellerId !== DG_RESELLER_ID &&
    isRenew

  useEffect(() => {
    const obj = parse(search)

    const licenseVersionId = obj.licenseVersionId as string
    const licenseId = (obj.licenseId as string) || ''
    const discountCode = (obj.discountCode as string) || ''
    const resellerId = (obj.resellerId as string) || ''

    if (licenseVersionId) {
      dispatch(getLicenseVersionsByIdThunk(licenseVersionId))
      dispatch(
        licenseVersionsPriceWithOptionsThunk({
          discountCode: discountCode,
          licenseVresionIds: [licenseVersionId],
        }),
      )
    }

    setIsRenew(Boolean(licenseId))

    if (licenseId) {
      dispatch(
        licenseVersionsPriceWithOptionsThunk({
          discountCode,
          licenseId,
        }),
      ).unwrap()

      setLicenseResellerId(resellerId)
    }
  }, [search])

  useEffect(() => {
    if (!licenseVersion && prices?.prices.length) {
      const version = prices?.prices.find((_, i: number) => i === 0)
      dispatch(setLicenseVersion(version?.licenseVersion))
    }
  }, [prices, licenseVersion])

  const subscription = useCallback(
    async (
      timePeriodId: string,
      isTrial: boolean,
      isFree: boolean,
      licenseIdForRenewal?: string | null,
    ) => {
      const obj = parse(search)
      const str = stringify({
        ...obj,
        timePeriodId,
        ...(licenseIdForRenewal && { licenseId: licenseIdForRenewal }),
      })

      const customer: CustomerModel = {
        customerEmail: obj.customerEmail as string,
        customerName: obj.customerName as string,
        customerSurname: obj.customerSurname as string,
        customerFarm: obj.customerFarm as string,
        countryCode: obj.countryCode as string,
        customerAddress: obj.customerAddress as string,
        customerPhone: obj.customerPhone as string,
        note: obj.note as string,
      }

      const license = obj.licenseVersionId
        ? { licenseVersionId: obj.licenseVersionId as string }
        : { licenseId: obj.licenseId as string }

      const licenseInfo = {
        discountCode: obj.discountCode as string,
        deviceSerial: (obj.serialNumber as string) || '',
        timePeriodId,
        ...license,
      }

      const data =
        role === RoleUserModel.Reseller
          ? { ...licenseInfo, ...customer }
          : licenseInfo

      if (isTrial) {
        setCurrentPeriod(timePeriodId)
        try {
          //TODO: handle trial case if we don't know the device sn whan BE is ready
          await dispatch(licensesTrialByDeviceSerialThunk(data)).unwrap()
          setSuccessFreeModal(true)
        } catch (errors) {
          setBackendError(errors)
        } finally {
          setCurrentPeriod('')
        }

        return
      }

      if (isFree) {
        setCurrentPeriod(timePeriodId)
        try {
          await dispatch(
            licensesFreeByDiscountCodeThunk({
              ...data,
              ...(isCreatedByDGOption && {
                createdByDg: isCreatedByDg,
              }),
            }),
          ).unwrap()
          setSuccessFreeModal(true)
        } catch (errors) {
          setBackendError(errors)
        } finally {
          setCurrentPeriod('')
        }

        return
      }

      navigate(`/cabinet/licenses/payment?${str}`)
      setItem('form', { ...obj, timePeriodId })
    },
    [
      dispatch,
      setSuccessFreeModal,
      setBackendError,
      isCreatedByDg,
      search,
      isCreatedByDGOption,
    ],
  )

  const onCloseModal = () => {
    dispatch(
      setToast({
        type: ToastType.success,
        message: t('The licence was successfully created'),
      }),
    )

    navigate(`/cabinet/licenses`)
  }

  return (
    <>
      <Helmet>
        <title>{t('Subscription')} | Registration</title>
      </Helmet>
      <div className={cnSubscriptionPage()}>
        <Back />
        <Title className={cnSubscriptionPage('title')}>
          {t('Please select and subscribe to one of the Packages')}
        </Title>
        {licenseVersion && (
          <div className={cnSubscriptionPage('subtitle')}>
            {t('Selected Product')}:{' '}
            <strong>
              "
              {LICENSE_VERSION_LABELS[licenseVersion.id] || licenseVersion.name}
              "
            </strong>
          </div>
        )}
        {isAnyPriceFree && (
          <div className={cnSubscriptionPage('subtitle')}>
            {t(
              'You can select any option, the invoice will be released by DG at your standard prices',
            )}
          </div>
        )}
        <div className={cnSubscriptionPage('wrap')}>
          {prices &&
            prices.prices.map(el => {
              // User can only have one license of a certain type at the time, so we activate redirect flow
              const isRenew =
                el.isAvaialbleToRenew &&
                el.isAlreadyActive &&
                el.latestLicenseId &&
                role === RoleUserModel.User

              return (
                <Block
                  key={el.timePeriodId}
                  className={cnSubscriptionPage('block')}
                >
                  <div className={cnSubscriptionPage('name')}>
                    {el.isFree
                      ? t(el.timePeriodId)
                      : el.isTrial
                      ? t('Trial')
                      : t(el.timePeriodId)}
                  </div>

                  <div className={cnSubscriptionPage('cost')}>
                    {el.coinsPrice > 0 && (
                      <sup className={cnSubscriptionPage('symbol')}>
                        {el.currency.symbol}
                      </sup>
                    )}
                    {el.isTrial
                      ? t('Free')
                      : el.isFree
                      ? t('Pay later')
                      : makeFraction(el.coinsPrice, el.currency.coinDigits)}
                  </div>

                  <div className={cnSubscriptionPage('term')}>
                    {t(`${el.timePeriodId}-term`)}
                  </div>

                  <div className={cnSubscriptionPage('text')}>
                    {el.isFree
                      ? t('Receive invoice at your standard price for', {
                          term: t(`${el.timePeriodId}-text`),
                        }) + t(' license from DG')
                      : el.isTrial
                      ? t('Free trial package for two months')
                      : isRenew
                      ? t('Renewal of the existing license')
                      : t('Full package for', {
                          term: t(`${el.timePeriodId}-text`),
                        })}
                  </div>

                  <Button
                    isLoading={currentPeriod === el.timePeriodId}
                    isDisabled={!!currentPeriod}
                    className={cnSubscriptionPage('btn')}
                    onClick={() =>
                      subscription(
                        el.timePeriodId,
                        el.isTrial,
                        el.isFree,
                        isRenew ? el.latestLicenseId : null,
                      )
                    }
                  >
                    {t('Subscribe for', {
                      term: t(`${el.timePeriodId}-text`),
                    })}
                  </Button>
                </Block>
              )
            })}
        </div>
        {isCreatedByDGOption && (
          <div className={cnSubscriptionPage('checkbox')}>
            <Checkbox
              label={t('Invoiced directly to final customer')}
              checked={isCreatedByDg}
              onChange={v => setIsCreatedByDg(v.target.checked)}
            />
          </div>
        )}

        <div className={cnSubscriptionPage('info')}>
          <img
            className={cnSubscriptionPage('img')}
            src='/img/modem 4G.jpg'
            alt='4G modem'
          ></img>
          <div className={cnSubscriptionPage('subscription')}>
            {t(
              'Do you want to exchange data wireless? Try our 4G modem. It is possible to buy a 4G to have your data always synchornized, fast and automatically. Email',
            )}
            <a href='mailto:sales@dinamicagenerale.com' target='_blank'>
              sales@dinamicagenerale.com
            </a>
            {t('to receive a quatation. More info at')}
            <a href={MODEM_4G_PRODUCT_LINK} target='_blank'>
              {MODEM_4G_PRODUCT_LINK}
            </a>
          </div>
        </div>
      </div>
      <Modal
        isOpen={successFreeModal}
        title={t(
          'Your activation is in progress! Please check your email, you will receive additional instruction to access your DTM account shortly. Inside your register account, you will have the possibility to renew your license.',
        )}
        titlePosition='center'
        size='md'
        onClose={() => onCloseModal()}
        isInform
        isButtonSmall
        closeBtnText='ok'
        buttonPosition='center'
      ></Modal>
    </>
  )
})
